export const checkIsMobile = () => {
  // 레이아웃 판정용 — "Mobi" 가 User agent에 포함되어 있으면 모바일.
  // 인앱은 기기 폭과 무관하게 폰 레이아웃이다(lockInAppViewport 가 레이아웃 뷰포트를 430 으로 고정).
  // 안드로이드 태블릿 웹뷰 UA 에는 "Mobi" 가 없어 앱 토큰을 함께 본다 — 없으면 태블릿 인앱이
  // PC 취급돼 대화방 엔터가 줄바꿈 대신 전송으로 동작한다(ChatInput handleKeyDownForSend).
  // 기기 "종류" 보고에는 쓰지 말 것 — 태블릿이 mobile 로 보고된다(fingerprint.ts 주석 참조).
  return /BubbleTapApp|Mobi/i.test(window.navigator.userAgent);
};

export const checkIsPortableDevice = () => {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
};

export const checkIsTouchDevice = () => {
  return /Mobi/i.test(window.navigator.userAgent) || window.navigator.maxTouchPoints > 0;
};

/**
 * 마우스 호버를 쓸 수 있는 기기인지 — 호버로만 유지되는 UI(잠깐 떴다 사라지는 버튼 등)의 분기용.
 * 저장소 CSS 관례(`@media (hover: hover) and (pointer: fine)`)와 같은 기준이다.
 * 터치 지원 노트북은 주 포인터가 마우스라 true 로 잡힌다 — UA·maxTouchPoints 를 보는 checkIsTouchDevice 와 용도가 다르다.
 */
export const checkCanHover = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;

  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
};

export const checkIsIosDevice = () => {
  return /iPhone|iPad|iPod|Mac/i.test(window.navigator.userAgent);
};

export const checkIsSafariBrowser = () => {
  let userAgentString = navigator.userAgent;
  let chromeAgent = userAgentString.indexOf('Chrome') > -1;
  let safariAgent = userAgentString.indexOf('Safari') > -1;

  return !(chromeAgent && safariAgent);
};

export const checkIsTargetStudioDynamicLink = () => {
  return checkIsMobile() && window.innerWidth < 980;
};

export const checkIsInBubbleTapApp = () => {
  return /BubbleTapApp/i.test(window.navigator.userAgent);
};

export const checkIsWindow = () => {
  return /Win/i.test(window.navigator.userAgent);
};

export const checkIsAosDevice = () => {
  const userAgent = navigator.userAgent;
  return /android/i.test(userAgent);
};

// ──────────────────────────────────────────────────────────────────────────
// PWA 설치/푸시 환경 분기용 감지 유틸
// ──────────────────────────────────────────────────────────────────────────

/** PWA(홈 화면 추가) standalone 모드로 실행 중인지. iOS Safari는 navigator.standalone 사용. */
export const checkIsStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  const isDisplayStandalone =
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches);
  const isIosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(isDisplayStandalone || isIosStandalone);
};

/** iPad 여부. iPadOS 13+ 는 데스크톱 모드 기본이라 "Macintosh" UA + 터치 지원으로도 판별한다. */
export const checkIsIpadDevice = (): boolean => {
  const ua = window.navigator.userAgent;

  if (/iPad/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && window.navigator.maxTouchPoints > 1;
};

/**
 * 데스크톱 Mac을 iOS로 오판하지 않는 "실제 iOS 단말" 판별.
 * (checkIsIosDevice는 정규식에 Mac을 포함하므로 데스크톱 Mac도 true가 됨)
 * iPadOS Safari의 Mac 위장은 checkIsIpadDevice 가 보정한다.
 */
export const checkIsRealIosDevice = (): boolean =>
  /iPhone|iPod/i.test(window.navigator.userAgent) || checkIsIpadDevice();

export type InAppBrowser = 'kakao' | 'instagram' | 'line' | 'facebook' | 'naver' | null;

/** 카카오/인스타/라인/페이스북/네이버 등 인앱(웹뷰) 브라우저 판별. 없으면 null. */
export const getInAppBrowser = (): InAppBrowser => {
  const ua = window.navigator.userAgent;
  if (/KAKAOTALK/i.test(ua)) return 'kakao';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/\bLine\//i.test(ua)) return 'line';
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'facebook';
  if (/NAVER\(inapp/i.test(ua)) return 'naver';
  return null;
};

export const checkIsInAppBrowser = (): boolean => getInAppBrowser() !== null;

/**
 * Meta 계열 인앱 웹뷰(Threads / Facebook / Instagram) 여부.
 *
 * 이 웹뷰들은 스크롤 오프셋이 0일 때 아래로 당기는 제스처를 가로채
 * pull-to-dismiss / pull-to-refresh 로 처리한다.
 * 대화방은 문서 스크롤 범위가 0이라 오프셋이 항상 0이므로 그대로 걸린다.
 * 세 앱 모두 같은 웹뷰 셸을 쓰며 Instagram 인앱에서도 동일 증상이 실기기로 확인됐다.
 *
 * getInAppBrowser 에 'threads' 를 추가하지 않고 별도 함수로 두는 이유:
 * 그 함수는 PWA 설치 분기(resolvePwaEnv)·외부 브라우저 열기에도 쓰여서,
 * 반환값이 늘면 스크롤과 무관한 동작까지 바뀐다.
 * (Barcelona = Threads 앱 웹뷰가 UA에 싣는 코드네임)
 */
export const checkIsMetaInAppWebView = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /Barcelona/i.test(ua) || /FBAN|FBAV|FB_IAB/i.test(ua) || /Instagram/i.test(ua);
};

/**
 * 실제 iOS Safari 여부.
 * iOS는 Safari에서만 "홈 화면에 추가"가 가능하다(Chrome/Firefox/Edge 등은 불가).
 * iOS Chrome(CriOS)/Firefox(FxiOS)/Edge(EdgiOS)/Opera(OPiOS)/Google앱(GSA)/Whale/삼성/네이버 등
 * 비-Safari iOS 브라우저와 인앱(웹뷰)을 제외한 순수 Safari만 true.
 */
export const checkIsIosSafari = (): boolean => {
  if (!checkIsRealIosDevice()) return false;
  if (getInAppBrowser() !== null) return false;
  const ua = window.navigator.userAgent;
  const isNonSafariIosBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|GSA|Whale|SamsungBrowser|NAVER/i.test(ua);
  if (isNonSafariIosBrowser) return false;
  return /Safari/i.test(ua);
};

/**
 * iOS 메이저.마이너 버전을 number로 반환 (예: 16.4 → 16.4). 파싱 불가 시 null.
 * 웹 푸시는 iOS 16.4+ & standalone 에서만 가능하므로 게이팅에 사용.
 */
export const getIosVersion = (): number | null => {
  const match = window.navigator.userAgent.match(/OS (\d+)[_.](\d+)/i);
  if (!match) return null;
  const major = Number(match[1]);
  const minor = Number(match[2]);
  if (Number.isNaN(major)) return null;
  return Number(`${major}.${Number.isNaN(minor) ? 0 : minor}`);
};
